import React, { useEffect } from 'react';
import { FormControl, Button, Grid, Paper, Typography, OutlinedInput, Box, FormLabel, FormHelperText, MenuItem, Select } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { postLabSpecimenTypeRequest, putLabSpecimenTypeRequest, resetPostLabSpecimenType, resetPutLabSpecimenType } from '../../../../../redux/modules/laboratory/labSpecimenType/slice';

const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    code: yup.string().required("Code is required").max(10, "Code must be at most 10 characters"),
    tubeType: yup.string().nullable(),
    tubeColor: yup.string().nullable(),
    volumeML: yup.number().nullable().positive("Must be positive").transform((value, originalValue) => originalValue === '' ? null : value),
    handlingInstructions: yup.string().nullable(),
    storageTemp: yup.string().nullable(),
    stabilityHours: yup.number().nullable().positive("Must be positive").transform((value, originalValue) => originalValue === '' ? null : value),
});

const tubeColors = ['red', 'lavender', 'green', 'yellow', 'blue', 'grey', 'gold', 'light_blue'];

const Form = ({ itemData, toggleList }) => {
    const dispatch = useDispatch();
    let { loading, postRes, putRes } = useSelector(state => state.labSpecimenType);
    const { register, handleSubmit, control, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: itemData || {}
    });

    useEffect(() => {
        if (postRes) {
            if (postRes?.success) { toggleList(); }
            toast(postRes?.message);
            dispatch(resetPostLabSpecimenType());
        }
        if (putRes) {
            if (putRes?.success) { toggleList(); }
            toast(putRes?.message);
            dispatch(resetPutLabSpecimenType());
        }
    }, [postRes, putRes, toggleList, dispatch]);

    const onSubmit = (data) => {
        if (itemData?._id) {
            dispatch(putLabSpecimenTypeRequest(data));
        } else {
            dispatch(postLabSpecimenTypeRequest(data));
        }
    };

    return (
        <Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
            <Paper sx={{ padding: 2, mt: 3 }} className='cardStyle'>
                <Typography variant="h5" className='title'>Specimen Type Form</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <FormLabel>Name</FormLabel>
                            <OutlinedInput className="inputfield" {...register('name')} size='small' placeholder="e.g. Serum" />
                            {errors?.name?.message && <FormHelperText error>{errors?.name?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <FormLabel>Code</FormLabel>
                            <OutlinedInput className="inputfield" {...register('code')} size='small' placeholder="e.g. SER" />
                            {errors?.code?.message && <FormHelperText error>{errors?.code?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <FormLabel>Tube Type</FormLabel>
                            <OutlinedInput className="inputfield" {...register('tubeType')} size='small' placeholder="e.g. Red top" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <FormLabel>Tube Color</FormLabel>
                            <Controller
                                name="tubeColor"
                                control={control}
                                defaultValue={itemData?.tubeColor || ''}
                                render={({ field }) => (
                                    <Select {...field} size="small" displayEmpty>
                                        <MenuItem value="">Select color</MenuItem>
                                        {tubeColors.map(c => (
                                            <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c.replace('_', ' ')}</MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Minimum Volume (mL)</FormLabel>
                            <OutlinedInput className="inputfield" {...register('volumeML')} size='small' type="number" placeholder="e.g. 3" />
                            {errors?.volumeML?.message && <FormHelperText error>{errors?.volumeML?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Storage Temperature</FormLabel>
                            <OutlinedInput className="inputfield" {...register('storageTemp')} size='small' placeholder="e.g. Room temp" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Stability (hours)</FormLabel>
                            <OutlinedInput className="inputfield" {...register('stabilityHours')} size='small' type="number" placeholder="e.g. 24" />
                            {errors?.stabilityHours?.message && <FormHelperText error>{errors?.stabilityHours?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormLabel>Handling Instructions</FormLabel>
                            <OutlinedInput className="inputfield" {...register('handlingInstructions')} size='small' placeholder="Special handling instructions" multiline rows={2} />
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mt: 3, justifyContent: { xs: 'center', sm: 'right' } }}>
                <Button className="commonBtn" type='submit' variant='contained' disabled={loading}>{itemData?._id ? "Update" : "Save"}</Button>
                <Button variant='outlined' className='btn cancelBtn' onClick={() => { toggleList() }}>Cancel</Button>
            </Box>
        </Box>
    );
};

export default Form;
